import { PlusCircleOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Modal } from "antd";
import { FormInstance } from "antd/lib";
import React from "react";
import { ContentModalSaveConfig, ModeSave, ModeSaveAsNew } from "./content-modal-save-config";
import { getUserInfo } from "@src/util/get-user-info";
import { IntelligenceFormFieldsEnum } from "../../enum";
import { ExplorationType } from "@src/pages/ab-test-explore/enum/exploration-types.enum";
import { FieldErrorWhenSave } from "./field-error-when-save";
import { isValueChanged } from "@src/util/intelligence-search/crud/is-form-values-change";
import { DuplicateConfigButton } from "@src/components/crud-explore/duplicate-config-button";
import { ShareConfigButton } from "@src/components/crud-explore/share-config-button";
import { useBlockNavigateUnSave } from "@src/hooks/use-block-navigate-unsave";

export type PayloadConfigType = {
  title: string | undefined;
  description: string | undefined;
  owner: any;
  config: any;
  type: ExplorationType;
  product_code: any;
};

export const SaveConfigComponent: React.FC<{
  form: FormInstance<any>;
  isCreate?: boolean;
  handleSave: (
    payload: PayloadConfigType,
    cb?: () => void,
    optionMode?: string,
    forceUpdate?: boolean,
  ) => Promise<void>;
  isLoading?: boolean;
  initConfig?: Record<string, any>;
  isOwnerConfig: boolean;
}> = ({ form, isCreate = false, handleSave, isLoading, initConfig = {}, isOwnerConfig = false }) => {
  const userInfo = getUserInfo();

  const [optionMode, setOptionMode] = React.useState<string>(ModeSave);

  const [titleConfig, setTitleConfig] = React.useState<{
    title?: string;
    description?: string;
  }>();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setOptionMode(isOwnerConfig ? ModeSave : ModeSaveAsNew);
  }, [isOwnerConfig]);

  React.useEffect(() => {
    if (isLoading) return;
    setTitleConfig({
      title: form.getFieldValue(IntelligenceFormFieldsEnum.TITLE),
      description: form.getFieldValue(IntelligenceFormFieldsEnum.DESCRIPTION),
    });
  }, [form, isLoading]);
  const handleCancel = () => {
    setOpen(false);
    setErrorFields([]);
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const [errorFields, setErrorFields] = React.useState<string[]>([]);

  const handleOk = async ({ forceSave }: { forceSave?: boolean }) => {
    const fieldsValue = [
      IntelligenceFormFieldsEnum.TITLE,
      IntelligenceFormFieldsEnum.DESCRIPTION,
      IntelligenceFormFieldsEnum.PRODUCT_CODE,
      IntelligenceFormFieldsEnum.PLATFORM,
      IntelligenceFormFieldsEnum.TIME_RANGE,
      IntelligenceFormFieldsEnum.FILTERS_SELECTED,
      IntelligenceFormFieldsEnum.QUERY_LIMIT,
      IntelligenceFormFieldsEnum.QUERY_ORDER,
    ].reduce((acc, key: string) => {
      if (key in form.getFieldsValue()) {
        acc[key] = form.getFieldsValue()[key];
      }
      return acc;
    }, {} as Record<string, any>);

    const productCode = fieldsValue?.[IntelligenceFormFieldsEnum.PRODUCT_CODE];
    const formErrorsField: string[] = [];
    if (!titleConfig?.title) {
      formErrorsField.push("Please input title!");
      setErrorFields(formErrorsField);
      return;
    }

    return await form
      .validateFields()
      .then(() => {
        handleSave(
          {
            title: titleConfig?.title,
            description: titleConfig?.description,
            owner: userInfo?.email,
            config: fieldsValue,
            type: ExplorationType.INTELLIGENCE_SEARCH,
            product_code: productCode,
          },
          () => handleCancel(),
          optionMode,
          optionMode === ModeSave || forceSave,
        );
      })
      .catch((err) => {
        formErrorsField.push(...err.errorFields.map((_err: any) => _err.errors).flat(1));
        setErrorFields(formErrorsField);
      });
  };
  const handleUpdateSoftTitleConfig = React.useCallback((title?: string, description?: string) => {
    setTitleConfig({
      title: title,
      description: description,
    });
  }, []);

  return (
    <>
      <Modal
        destroyOnHidden
        title={isCreate ? "Create new exploration" : "Save exploration"}
        centered
        open={open}
        onCancel={handleCancel}
        onOk={() =>
          handleOk({
            forceSave: false,
          })
        }
      >
        <FieldErrorWhenSave errorFields={errorFields} />
        <ContentModalSaveConfig
          form={form}
          handleUpdateSoftTitleConfig={handleUpdateSoftTitleConfig}
          showMode={!isCreate}
          isOwnerConfig={isOwnerConfig}
          optionMode={optionMode}
          setOptionMode={setOptionMode}
        />
      </Modal>
      <ActionButton
        isCreate={isCreate}
        initConfig={initConfig}
        handleOpen={handleOpen}
        form={form}
        handleOk={handleOk}
        isOwnerConfig={isOwnerConfig}
      />
    </>
  );
};

const ActionButton: React.FC<{
  isCreate: boolean;
  initConfig: Record<string, any>;
  handleOpen: () => void;
  form: FormInstance;
  handleOk: ({ forceSave }: { forceSave?: boolean }) => Promise<void>;
  isOwnerConfig: boolean;
}> = ({ isCreate, initConfig, handleOpen, form, handleOk, isOwnerConfig }) => {
  const { setWarnWhen } = useBlockNavigateUnSave();

  const titleWatch = Form.useWatch(IntelligenceFormFieldsEnum.TITLE, form);
  const descriptionWatch = Form.useWatch(IntelligenceFormFieldsEnum.DESCRIPTION, form);
  const productCodeWatch = Form.useWatch(IntelligenceFormFieldsEnum.PRODUCT_CODE, form);
  const platformWatch = Form.useWatch(IntelligenceFormFieldsEnum.PLATFORM, form);
  const timeRangeWatch = Form.useWatch(IntelligenceFormFieldsEnum.TIME_RANGE, form);
  const filterSelectedWatch = Form.useWatch(IntelligenceFormFieldsEnum.FILTERS_SELECTED, form);
  const queryLimitWatch = Form.useWatch(IntelligenceFormFieldsEnum.QUERY_LIMIT, form);
  const queryOrderWatch = Form.useWatch(IntelligenceFormFieldsEnum.QUERY_ORDER, form);

  const isConfigChanging =
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.TITLE], titleWatch) ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.DESCRIPTION], descriptionWatch) ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.PRODUCT_CODE], productCodeWatch) ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.PLATFORM], platformWatch, "array") ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.TIME_RANGE], timeRangeWatch, "timeRange") ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.FILTERS_SELECTED], filterSelectedWatch, "array") ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.QUERY_ORDER], queryOrderWatch, "array") ||
    isValueChanged(initConfig?.[IntelligenceFormFieldsEnum.QUERY_LIMIT], queryLimitWatch);
  const isDisabled = isOwnerConfig && !isConfigChanging;
  const isFieldTouched = form.isFieldsTouched();
  React.useEffect(() => {
    if (isCreate) {
      setWarnWhen(isFieldTouched);
    } else {
      setWarnWhen(isConfigChanging);
    }
  }, [isConfigChanging, isCreate, isFieldTouched, setWarnWhen]);

  return (
    <div className="flex space-x-2 items-center">
      {isOwnerConfig && (
        <>
          <ShareConfigButton
            page="intelligence-search"
            handleClick={() =>
              handleOk({
                forceSave: true,
              })
            }
            hasConfigChanged={isConfigChanging}
            isPublic={initConfig?.isPublic}
            productCode={initConfig?.[IntelligenceFormFieldsEnum.PRODUCT_CODE]}
          />
          <DuplicateConfigButton
            handleClick={() =>
              handleOk({
                forceSave: true,
              })
            }
            hasConfigChanged={isConfigChanging}
            page="intelligence-search"
          />
        </>
      )}

      <Button
        icon={isCreate ? <PlusCircleOutlined /> : <SaveOutlined />}
        style={isDisabled ? {} : { background: "#FFD666", borderColor: "#FAAD14", color: "black" }}
        disabled={isDisabled}
        type="primary"
        onClick={handleOpen}
      >
        {isCreate || !isOwnerConfig ? "Save New" : "Save"}
      </Button>
    </div>
  );
};
